import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DeclarationsService, HistoricDeclarationFilters } from '../../../core/services/declarations/declarations.service';
import { faArrowDownToLine } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSquareCheck } from '@fortawesome/pro-light-svg-icons';
import { PaginationComponent } from '../../../components/pagination/pagination.component';
import { Router } from '@angular/router';
import { FiltersService } from '../../../core/services/declarations/filters.service';
import { PresentationTypeEnum } from '../../../core/enums/presentation-type.enum';
import { PrimeTableComponent, HistoricTableFilters } from "../../../components/prime-table/prime-table.component";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DEPARTMENTS_TYPES } from '../../../core/constants/client.constants';
import { CommentsDeclarationsComponent } from "../../../components/comments-declarations/comments-declarations.component";
import { StatusDeclarationEnum } from '../../../core/enums/status-declaration.enum';
import { AuthService } from '../../../core/services/auth.service';

const HISTORIC_FILTERS_STORAGE_KEY = 'historicTableFilters';

@Component({
  selector: 'app-declarations',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, PaginationComponent, PrimeTableComponent, ProgressSpinnerModule, CommentsDeclarationsComponent],
  templateUrl: './declarations.component.html',
  styleUrl: './declarations.component.scss'
})
export class DeclarationsComponent {

  faSquareCheck = faSquareCheck;
  faDownload = faArrowDownToLine;

  isLoading = false;
  historicCampaigns = false;
  tabs = [
    { title: 'En curso', isActive: true },
    { title: 'Histórico', isActive: false }
  ];

  clients = [];
  users = [];
  models: any = undefined;
  fases = [];
  years = [];
  applications = [];
  appCodes = [];

  presentationTypesEnum = PresentationTypeEnum;
  presentationTypes = Object.keys(this.presentationTypesEnum).map(type => ({
    label: this.presentationTypesEnum[type],
    value: type,
  }));

  statusOptions = [
    { label: 'Realizada', value: StatusDeclarationEnum.Finished },
    { label: 'No procede', value: StatusDeclarationEnum.NotApplicable },
  ];

  departmentsTypes = DEPARTMENTS_TYPES;
  userDepartment;

  headers = [
    { name: 'Checkbox', key: 'checkbox', type: 'checkbox' },
    { name: 'Modelo', key: 'model', type: 'list' },
    { name: 'Cod.', key: 'client', type: 'text' },
    { name: 'App Code', key: 'codeApplication', type: 'text' },
    { name: 'Aplicación', key: 'application', type: 'text' },
    { name: 'Cliente', key: 'clientName', type: 'list' },
    { name: 'Avisos', key: 'hasNotifications', type: 'badge' },
    { name: 'Presentación', key: 'presentationType', type: 'list' },
    { name: 'Fase', key: 'tasks', type: 'list' },
    { name: 'Responsable', key: 'responsible', type: 'list' },
    { name: 'Ejecutor', key: 'executor', type: 'list' },
    { name: 'Comentarios', key: 'observations', type: 'list' },
    { name: 'Cuenta bancaria', key: 'bankAccount', type: 'text' }
  ];

  historicHeaders = [
    { name: 'Modelo', key: 'model' },
    { name: 'Cod. Cliente', key: 'client' },
    { name: 'Cliente', key: 'clientName' },
    { name: 'Avisos', key: 'hasNotifications' },
    { name: 'Tipo presentación', key: 'presentationType' },
    { name: 'Estado', key: 'state' },
    { name: 'Responsable', key: 'responsible' },
    { name: 'Ejecutor', key: 'executor' },
    { name: 'Fecha de finalización', key: 'dateFinish' },
    { name: 'Comentarios', key: 'observations' },
    { name: 'Tipo decl.', key: 'newDeclaration' },
    { name: 'Log', key: 'log' },
  ];

  currentPage = 0;
  itemsForPage = 50;
  maxPages = undefined;

  historicFilters: HistoricTableFilters = this.createDefaultHistoricFilters();

  configPrimeTable: any = {
    headers: this.headers,
    data: undefined,
  };

  configHistoricPrimeTable: any = {
    headers: this.historicHeaders,
    data: undefined,
    serverSide: true,
    historic: true,
    activeFilters: this.historicFilters,
    filtersData: undefined,
  };

  selectedDeclarations = [];

  constructor(
    private declarationsService: DeclarationsService,
    private filtersService: FiltersService,
    private router: Router,
    private authService: AuthService,
  ) {
    const filters = JSON.parse(localStorage.getItem('filters') ?? 'null');
    if (filters?.historicCampaigns) {
      this.historicCampaigns = filters.historicCampaigns;
    }
    this.restoreHistoricFilters();
  }

  createDefaultHistoricFilters(): HistoricTableFilters {
    return {
      models: [],
      clients: [],
      clientCodes: [],
      presentationTypes: [],
      status: [],
      hasNotifications: null,
      hasComments: null,
      responsibles: [],
      executors: [],
      selectedPeriod: [],
      relatedDeclarationType: null,
      year: null,
    };
  }

  restoreHistoricFilters(): void {
    const saved = localStorage.getItem(HISTORIC_FILTERS_STORAGE_KEY);
    if (!saved) {
      return;
    }
    try {
      const parsed = JSON.parse(saved);
      this.historicFilters = {
        ...this.createDefaultHistoricFilters(),
        ...parsed,
        selectedPeriod: parsed.selectedPeriod?.map((d: string | null) => (d ? new Date(d) : null)) ?? [],
      };
    } catch {
      this.historicFilters = this.createDefaultHistoricFilters();
    }
  }

  saveHistoricFilters(): void {
    localStorage.setItem(HISTORIC_FILTERS_STORAGE_KEY, JSON.stringify(this.historicFilters));
  }

  async ngOnInit() {
    const user = this.authService.getUser();
    this.clients = await this.filtersService.getClients();
    this.users = (await this.filtersService.getUsers()).filter(u =>
      user?.user?.department !== 'GENERAL' ? u.department === user?.user?.department : u.department
    );
    const maxPhases = await this.filtersService.getMaxPhases();
    this.models = (await this.filtersService.getModels()).filter(model =>
      user?.user?.department !== 'GENERAL' ? model.department === user?.user?.department : model.department
    );
    this.models.forEach(model => {
      model.department = this.getDepartmentLabel(model.department);
      model.models = model.models.sort((a, b) => a.name.localeCompare(b.name));
    });
    const minYear = await this.filtersService.getMinimumYear();
    const minYearNumber = Number(minYear.minYear);
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < maxPhases.maxPhases; i++) {
      this.fases.push({ name: 'Fase ' + (i + 1), id: (i + 1), selected: false });
    }
    for (let i = minYearNumber; i <= currentYear; i++) {
      this.years.push(i);
    }

    this.configHistoricPrimeTable.filtersData = {
      clients: [...this.clients].sort((a, b) => a.name.localeCompare(b.name)),
      models: this.models,
      users: [...this.users].sort((a, b) => a.name.localeCompare(b.name)),
      presentationTypes: this.presentationTypes,
      statusOptions: this.statusOptions,
      years: this.years,
    };

    const tab = JSON.parse(localStorage.getItem('activeTab') ?? 'null');
    if (tab) {
      await this.changeTab(tab);
    }
    this.userDepartment = this.authService.getUser()?.user?.department;
    await this.loadPage();
  }

  async changeTab(tab: any) {
    this.tabs = this.tabs.map((t: any) => {
      t.isActive = t.title === tab.title;
      localStorage.setItem('activeTab', JSON.stringify(tab));
      return t;
    });
    this.historicCampaigns = this.tabs[1].isActive;
    this.currentPage = 0;
    await this.loadPage();
  }

  buildHistoricApiFilters(): HistoricDeclarationFilters {
    const startDate = this.historicFilters.selectedPeriod?.[0]
      ? new Date(this.historicFilters.selectedPeriod[0]).toISOString()
      : undefined;
    const endDate = this.historicFilters.selectedPeriod?.[1]
      ? new Date(this.historicFilters.selectedPeriod[1]).toISOString()
      : undefined;

    return {
      department: this.userDepartment,
      page: this.currentPage,
      itemsForPage: this.itemsForPage,
      clients: (this.historicFilters.clients ?? []).map(id => ({ id })),
      clientCodes: this.historicFilters.clientCodes,
      models: this.historicFilters.models,
      presentations: this.historicFilters.presentationTypes,
      withComments: this.historicFilters.hasComments,
      year: this.historicFilters.year ?? undefined,
      relatedDeclarations: this.historicFilters.relatedDeclarationType ?? undefined,
      startPeriod: startDate,
      endPeriod: endDate,
      responsibles: this.historicFilters.responsibles,
      executors: this.historicFilters.executors,
      status: this.historicFilters.status,
      hasNotifications: this.historicFilters.hasNotifications,
    };
  }

  async downloadResults() {
    await this.declarationsService.getDownloadHistoricDeclarations(this.buildHistoricApiFilters());
  }

  async getData() {
    try {
      await this.fetchData();
    } finally {
      this.isLoading = false;
    }
  }

  private async fetchData() {
    if (!this.userDepartment) {
      return;
    }

    this.selectedDeclarations = [];

    if (this.historicCampaigns) {
      this.isLoading = true;
      const data = await this.declarationsService.getHistoricDeclarations(this.buildHistoricApiFilters());
      this.maxPages = data.totalPages + 1;
      this.configHistoricPrimeTable = {
        ...this.configHistoricPrimeTable,
        headers: this.historicHeaders,
        data,
        activeFilters: { ...this.historicFilters },
        filtersData: this.configHistoricPrimeTable.filtersData,
      };
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    const data = await this.declarationsService.getDeclarations(this.userDepartment);
    data.declarations = data.declarations.map(declaration => {
      const clientApplications = declaration.client?.client_applications?.[0];
      let applicationCode = null;
      const applicationName = declaration.model.application;

      if (clientApplications && applicationName) {
        switch (applicationName) {
          case 'A3Soc': applicationCode = clientApplications.a3SocCode; break;
          case 'A3Ren': applicationCode = clientApplications.a3RenCode; break;
          case 'A3Nom': applicationCode = clientApplications.a3NomCode; break;
          case 'A3Eco': applicationCode = clientApplications.a3EcoCode; break;
          case 'A3Ges': applicationCode = clientApplications.a3GesCode; break;
          default:
            applicationCode = declaration.client?.applications?.find(app => app.name === applicationName)?.code;
            break;
        }
      } else {
        applicationCode = declaration.client?.applications?.find(app => app.name === applicationName)?.code;
      }

      return {
        ...declaration,
        isChecked: false,
        application: { code: applicationCode, name: applicationName },
      };
    });

    this.applications = [...new Set(data.declarations.map(d => d.application?.name).filter(Boolean))];
    this.appCodes = [...new Set(data.declarations.map(d => d.application?.code).filter(Boolean))];
    this.configPrimeTable = {
      ...this.configPrimeTable,
      headers: this.headers,
      data,
      filtersData: {
        clients: this.clients,
        applications: this.applications,
        applicationCodes: this.appCodes,
        fases: this.fases,
        models: this.models,
        years: this.years,
        presentationTypes: this.presentationTypes,
        users: this.users,
      },
    };
    this.isLoading = false;
  }

  async loadPage() {
    await this.getData();
  }

  async onServerFilterChange(filters: HistoricTableFilters) {
    this.historicFilters = { ...filters };
    this.saveHistoricFilters();
    this.currentPage = 0;

    // El calendario en modo rango emite [inicio, null] tras el primer clic: esperar a la fecha fin.
    if (filters.selectedPeriod?.length && !filters.selectedPeriod[1]) {
      return;
    }

    await this.getData();
  }

  async onHistoricPageChange(page: number) {
    this.currentPage = page;
    await this.getData();
  }

  onDeclarationChange(declaration: any) {
    if (declaration.isChecked && !this.selectedDeclarations.includes(declaration.id)) {
      this.selectedDeclarations.push(declaration.id);
    } else if (!declaration.isChecked) {
      this.selectedDeclarations = this.selectedDeclarations.filter(d => d !== declaration.id);
    }
  }

  goToMultipleGestion() {
    this.router.navigate(['/declarations/multiple-gestion'], {
      queryParams: { ids: this.selectedDeclarations.join(',') }
    });
  }

  async createRelatedDeclaration(event) {
    await this.declarationsService.createAsociedDeclaration(event[0], event[1]);
    await this.getData();
  }

  getDepartmentLabel(departmentValue: string): string {
    const department = this.departmentsTypes.find(dep => dep.value === departmentValue);
    return department ? department.label : '';
  }
}
